/**
 * Main layout — top bar, bottom tab bar, floating Add Lift button, upload progress strip, announcement banner.
 * Uses the new design-system primitives. Tab count, order, and route paths preserved exactly.
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUpload } from '../../features/upload/UploadContext';
import { AnnouncementBanner } from './AnnouncementBanner';
import TopBar from './TopBar.jsx';
import TabBar from './TabBar.jsx';
import FAB from './FAB.jsx';
import styles from './MainLayout.module.css';

const TABS = [
  { to: '/',            label: 'Home',        icon: 'home',    end: true },
  { to: '/coaching',    label: 'Coaching',    icon: 'coach'               },
  { to: '/leaderboard', label: 'Leaderboard', icon: 'trophy'              },
  { to: '/search',      label: 'Search',      icon: 'search'              },
  { to: '/profile',     label: 'Profile',     icon: 'user'                },
];

export function MainLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { progress } = useUpload();
  const [, setBannerVisible] = useState(false);

  return (
    <>
      <AnnouncementBanner onVisibilityChange={setBannerVisible} />
      {progress != null && (
        <div className={styles.uploadBar} role="status" aria-live="polite">
          <div
            className={styles.uploadBarFill}
            style={{
              width: `${Math.max(progress.status === 'compressing' ? 5 : 0, progress.percent ?? 0)}%`,
            }}
          />
          <span className={styles.uploadBarLabel}>
            {progress.status === 'error' && (progress.error || 'Upload failed')}
            {progress.status === 'compressing' && `Compressing… ${progress.percent ?? 0}%`}
            {progress.status === 'uploading' && `Uploading… ${progress.percent}%`}
            {progress.status === 'creating' && (progress.type === 'challenge' ? 'Saving…' : 'Saving lift…')}
          </span>
        </div>
      )}

      <TopBar
        left={
          <Link to="/" aria-label="BarChain home" className={styles.logoLink}>
            <img src="/logo.png" alt="BarChain" className={styles.logo} />
          </Link>
        }
      />

      <main className={styles.main}>{children}</main>

      {location.pathname !== '/add' && (
        <FAB label="Add lift" onClick={() => navigate('/add')} />
      )}

      <a
        href="https://www.bugsquads.com"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.poweredBy}
      >
        Powered by{' '}<span className={styles.poweredByName}>BugSquads</span><span className={styles.poweredByDot}>.</span>
      </a>

      <TabBar items={TABS} />
    </>
  );
}
